<template>
  <div class="w-full h-full">
    <div class="flex justify-between items-center px-3">
      <AppTooltip content="This is the total account value, pending transactions are included in this total." placement="right" :width="225">
        <div class="flex items-center gap-1">
          <div class="font-semibold text-lg">Balance</div>
          <InformationCircleIcon class="size-5 mt-0.5 text-gray-500" />
        </div>
      </AppTooltip>

      <div class="flex flex-col items-end">
        <div class="font-semibold text-xl tracking-wide mr-2">{{ dollarize(aggregated_balance, true) }}</div>
        <div class="h-5">
          <div v-if="available_aggregated_balance" class="text-xs text-gray-500 tracking-wide mr-2 mt-1"> Settled Cash: {{ dollarize(available_aggregated_balance, true) }}</div>
        </div>
        
      </div>
    </div>
    <div class="pb-5 px-3 h-[95%]">
      <Line :data="chart_data" :options="chart_options" />
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, watch, onMounted } from "vue";

// Components
import AppTooltip from "@/components/applicationui/AppTooltip.vue";

// Icons
import { InformationCircleIcon } from "@heroicons/vue/24/outline";

// Libraries
import { Line } from "vue-chartjs";
import { hexToRgba } from "@/helper/helper.js";
import { dollarize } from "@/helper/format.js";

// Stores
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();
import { useTransactionsStore } from "@/stores/transactions";
const transactionsStore = useTransactionsStore();

// Props
const props = defineProps({
  accounts: {
    type: Array,
    required: true,
  },
});

// Draw Line Chart START
const operacolor = getComputedStyle(document.documentElement).getPropertyValue("--operacolor");
const operacolor_rgba = hexToRgba(operacolor, 0.3);
const chart_data = ref({
  labels: ["Account Opened", "Today"],
  datasets: [
    {
      label: "Account Value",
      data: [0, 0],
      fill: true,
      borderColor: operacolor,
      backgroundColor: operacolor_rgba,
      tension: 0.01,
      pointStyle: "circle",
      pointRadius: 6,
      pointHoverRadius: 10,
    },
  ],
});
const chart_options = ref({
  maintainAspectRatio: false,
  responsive: true,
  interaction: { intersect: false },
  plugins: {
    tooltip: {
      callbacks: {
        label: function (tooltipItem) {
          return dollarize(tooltipItem.raw);
        },
      },
    },
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      display: true,
      suggestedMin: 0,
      ticks: {
        // Include a dollar sign in the ticks
        callback: function (value) {
          return dollarize(value, false);
        },
        maxTicksLimit: 5
      },
      border: {
        dash: [5, 5], // Dashed main Y-axis line
      },
    },
    x: {
      border: {
        dash: [5, 5], // Dashed main Y-axis line
      },
    },
  },
});
async function drawLineChart(dates, monthlyValues) {
  chart_data.value = {
    labels: dates,
    datasets: [
      {
        label: "Account Value",
        data: monthlyValues,
        fill: true,
        borderColor: operacolor,
        backgroundColor: operacolor_rgba,
        tension: 0.01,
        pointStyle: "circle",
        pointRadius: 6,
        pointHoverRadius: 10,
      },
    ],
  };

  // Calculate max value and dynamic step size
  const maxValue = Math.max(...monthlyValues);
  const stepSize = Math.round(maxValue / 3);

  // Update the Y-axis ticks stepSize
  chart_options.value.scales.y.ticks.stepSize = stepSize;
}
// Draw Line Chart END

// Get all transactions START
async function getMonthlyAccountValues() {
  const transactions = [];
  const accounts = props.accounts;

  // Fetch all transactions from each account
  for (const account of accounts) {
    const accountTransactions = await transactionsStore.getAllTransactionByAccountId(account.id);
    if (accountTransactions) transactions.push(...accountTransactions);
  }

  // Get the earliest account creation date
  const earliestDate = accounts.reduce(
    (earliest, account) => (account.created_at < earliest ? account.created_at : earliest),
    new Date().toISOString(),
  );

  const monthlyValues = [];
  const dates = [];

  // Check if the earliestDate is in the same month as the current month
  const today = new Date();
  const earliestAccountDate = new Date(earliestDate);
  if (earliestAccountDate.getMonth() === today.getMonth() && earliestAccountDate.getFullYear() === today.getFullYear()) {
    // const yesterday = new Date(today);
    // yesterday.setDate(today.getDate() - 1);

    // dates.push(yesterday.toISOString().split('T')[0]);
    dates.push(today.toISOString().split("T")[0]);

    monthlyValues.push(0);
  } else {
    // Generate month-start dates from the earliest account creation to now
    let current = new Date(today.getFullYear(), today.getMonth(), 1);
    const start = new Date(new Date(earliestDate).getFullYear(), new Date(earliestDate).getMonth(), 1);

    while (current >= start) {
      dates.unshift(current.toISOString().split("T")[0]);
      current.setMonth(current.getMonth() - 1);
    }
  }

  // Sort transactions by created_at timestamp
  transactions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  let totalBalance = 0;
  dates.forEach((date, index) => {
    // Apply transactions up to the current date
    for (const tx of transactions) {
      if (tx.created_at <= date + "T23:59:59.999") {
        const transaction_total = tx.amount * tx.price_per_unit;
        if (tx.type == "deposit") {
          totalBalance += transaction_total;
        } else if (tx.type == "withdraw") {
          totalBalance -= transaction_total;
        }
        // else if (tx.type == "buy") {
        //   totalBalance -= transaction_total;
        // } else if (tx.type == "sell") {
        //   totalBalance += transaction_total;
        // }
        else if (tx.type == "refund") {
          totalBalance += transaction_total;
        } else if (tx.type == "distribution") {
          totalBalance += transaction_total;
        }
      }
    }
    if (monthlyValues[index] === 0) {
      monthlyValues[index] = totalBalance;
    } else {
      monthlyValues.push(totalBalance);
    }
  });

  if (dates.length <= 1) {
    dates.unshift("Account Opened");
    monthlyValues.unshift(0);
  }

  return { dates, monthlyValues };
}
// Get all transactions END

// Get Account Balances START
const aggregated_balance = ref(0);
const available_aggregated_balance = ref(0);
const getAccountBalances = async () => {
  aggregated_balance.value = 0;
  props.accounts.forEach(async (account) => {
    const balance = await accountsStore.getAccountBalance(account.id);
    aggregated_balance.value += balance;
  });
};
const getAccountAvailableCashBalance = async () => {
  available_aggregated_balance.value = 0;
  props.accounts.forEach(async (account) => {
    const balance = await accountsStore.getAccountAvailableCashBalance(account.id);
    available_aggregated_balance.value += balance;
    if (available_aggregated_balance.value < 0) available_aggregated_balance.value = 0;
  });
};
// Get Account Balances END

// Mounted START
const loading = ref(true);
onMounted(async () => {
  getAccountBalances();
  getAccountAvailableCashBalance();

  const { dates, monthlyValues } = await getMonthlyAccountValues();
  drawLineChart(dates, monthlyValues);

  // Ready
  loading.value = false;
});
// Mounted END

// Watchers
watch(
  () => props.accounts,
  async () => {
    getAccountBalances();
    getAccountAvailableCashBalance();
    const { dates, monthlyValues } = await getMonthlyAccountValues();
    drawLineChart(dates, monthlyValues);
  },
);
</script>
